import React from 'react';
import PropTypes from 'prop-types';
import ReactModal from 'react-modal';
import classNames from 'classnames';
import { useModal } from '../../contextProviders';

import Icon from '../Icon';

import './Modal.css';

if (typeof document !== 'undefined') {
  ReactModal.setAppElement(document.getElementById('root'));
}

const Modal = ({
  className='',
  closeButton,
  fullsize=false,
  overflow=true,
  shouldCloseOnEsc,
  isOpen,
  title,
  onClose,
  children,
  shouldCloseOnOverlayClick,
}) => {
  const { hide } = useModal();

  const handleClose = () => {
    hide();
  };

  const renderHeader = () => {
    return (
      title && (
        <header
          className="flex items-center justify-between px-6 py-5 rounded-t-2xl"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-1 h-5 rounded-full flex-shrink-0"
              style={{ background: 'linear-gradient(180deg, #3b82f6, #6366f1)' }}
            />
            <span
              className="text-sm font-bold uppercase tracking-widest flex grow"
              style={{ color: '#818cf8' }}
              data-cy="modal-header"
            >
              {title}
            </span>
          </div>

          {closeButton && (
            <button
              onClick={onClose}
              className="flex items-center justify-center w-8 h-8 rounded-lg transition-all"
              style={{ background: 'rgba(255,255,255,0.05)', color: '#94a3b8' }}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                e.currentTarget.style.color = '#fff';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                e.currentTarget.style.color = '#94a3b8';
              }}
            >
              <Icon name="close" className="w-3.5 h-3.5 cursor-pointer" />
            </button>
          )}
        </header>
      )
    );
  };

  return (
    <ReactModal
      className={classNames(
        className,
        'relative max-h-full w-11/12 text-white outline-none lg:w-10/12 xl:w-9/12 rounded-2xl',
        {'h-full flex flex-col': fullsize}
      )}
      style={{
        content: {
          background: 'rgba(11, 15, 25, 0.98)',
          border: '1px solid rgba(99,102,241,0.2)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 40px rgba(99,102,241,0.1)',
          padding: 0,
        }
      }}
      overlayClassName="fixed top-0 left-0 right-0 bottom-0 z-[1000] flex items-start justify-center py-16 backdrop-blur-md bg-black/70 transition-opacity"
      shouldCloseOnEsc={shouldCloseOnEsc}
      onRequestClose={handleClose}
      isOpen={isOpen}
      title={title}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
    >
      {renderHeader()}
      <section className={classNames(
        'modal-content px-6 pt-4 pb-6 rounded-b-2xl',
        {'flex flex-col flex-1': fullsize},
        {'overflow-y-auto ohif-scrollbar': overflow},
      )}>
        {children}
      </section>
    </ReactModal>
  );
};

Modal.defaultProps = {
  shouldCloseOnEsc: true,
  shouldCloseOnOverlayClick: true,
};

Modal.propTypes = {
  closeButton: PropTypes.bool,
  shouldCloseOnEsc: PropTypes.bool,
  isOpen: PropTypes.bool,
  title: PropTypes.string,
  onClose: PropTypes.func,
  /** The modal's content */
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  shouldCloseOnOverlayClick: PropTypes.bool,
};

export default Modal;
